import type { Language, LocalizedText } from "../../components/ui/LanguageProvider";
import { pickLanguage } from "../../components/ui/LanguageProvider";

const CRM_SECTION_LABELS: Record<string, LocalizedText> = {
  overview: { ko: "개요", en: "Overview" },
  sales: { ko: "영업", en: "Sales" },
  opportunity: { ko: "Opportunity", en: "Opportunity" },
  project: { ko: "Project", en: "Project" },
  // ★ STEP 4: 문서 10줄을 통합 작성 화면으로 옮기면서 섹션이 "문서 영역 입구"가 됐다.
  "document-automation": { ko: "문서", en: "Documents" },
  template: { ko: "템플릿", en: "Templates" },
  test: { ko: "테스트", en: "Tests" },
};

const CRM_MENU_LABELS: Record<string, LocalizedText> = {
  dashboard: { ko: "대시보드", en: "Dashboard" },
  "marketing-dashboard": { ko: "마케팅 대시보드", en: "Marketing Dashboard" },
  "automation-roadmap": { ko: "자동화 로드맵", en: "Automation Roadmap" },
  leads: { ko: "리드 인박스", en: "Lead Inbox" },
  "crm-files": { ko: "CRM 파일", en: "CRM Files" },
  partners: { ko: "파트너사", en: "Partners" },
  // 회사·담당자 통합. 국문 표기는 쓰지 않는다(사용자 결정) — 한국어 모드도 영문 그대로.
  relationships: { ko: "Relationships", en: "Relationships" },
  pipeline: { ko: "영업 기회", en: "Opportunities" },
  "partner-capabilities": { ko: "파트너 역량", en: "Partner Capabilities" },
  "partner-verification": { ko: "파트너 적합성 평가", en: "Partner Verification" },
  "partner-analysis": { ko: "파트너 분석", en: "Partner Analysis" },
  // /crm/projects 가 예전 opportunities 경로였던 시절의 잔재였다. 지금은 수주 이후
  //   관리 화면이라 이름이 실제와 어긋나 있었다.
  projects: { ko: "Projects", en: "Projects" },
  // 같은 이름이 두 섹션에 있으면 어느 쪽인지 구분이 안 된다(docs/ux-laws/HICKS_LAW.md 규칙 6).
  "pipeline-settings": { ko: "영업 파이프라인 설정", en: "Sales Pipeline Settings" },
  "project-pipeline-settings": { ko: "프로젝트 파이프라인 설정", en: "Project Pipeline Settings" },
  "opportunity-overview": { ko: "Opportunity 현황", en: "Opportunity Overview" },
  // 통합 작성 화면(/crm/documents/:docKey) — 문서 11종을 한 화면에서 전환하며 쓴다.
  documents: { ko: "문서 템플릿", en: "Document Templates" },
  "3d-simulation": { ko: "3D Simulation", en: "3D Simulation" },
  event: { ko: "이벤트(참석자)", en: "Events (Attendees)" },
  "company-assets": { ko: "회사 자료", en: "Company Assets" },
  "logistics-data-lab": { ko: "현장 데이터 분석", en: "Site Data Analysis" },
  "communication-templates": { ko: "커뮤니케이션 템플릿", en: "Communication Templates" },
  "email-templates": { ko: "이메일", en: "Email" },
  "slack-templates": { ko: "Slack", en: "Slack" },
  "lead-mail-rules": { ko: "리드 메일 규칙", en: "Lead Mail Rules" },
  "email-send": { ko: "이메일 발송 테스트", en: "Email Send Test" },
  "lead-api": { ko: "Lead API(VAULT)", en: "Lead API(VAULT)" },
  "proposal-form-sample": { ko: "제안서 양식 샘플", en: "Proposal Form Sample" },
};

const CRM_DOCUMENT_LABELS: Record<string, LocalizedText> = {
  "quotation-group": { ko: "견적서", en: "Quotation" },
  quote: { ko: "시스템 견적서", en: "System Quote" },
  "quote-other": { ko: "자동화 견적서", en: "Automation Quote" },
  "quote-equipment": { ko: "장비 견적서", en: "Equipment Quote" },
  "contract-group": { ko: "계약서", en: "Contract" },
  contract: { ko: "WMS 계약서", en: "WMS Contract" },
  "contract-integration": { ko: "시스템 연동 계약서", en: "System Integration Contract" },
  invoice: { ko: "인보이스", en: "Invoice" },
  "mou-group": { ko: "MOU", en: "MOU" },
  mou: { ko: "파트너십 MOU", en: "Partnership MOU" },
  "maintenance-mou": { ko: "유지보수 MOU", en: "Service MOU" },
  "mou-dematic": { ko: "Dematic MOU", en: "Dematic MOU" },
  nda: { ko: "NDA", en: "NDA" },
  "nda-international": { ko: "해외 NDA", en: "International NDA" },
  "order-approval": { ko: "수주품의서", en: "Sales Approval Form" },
  proposal: { ko: "제안서", en: "Proposal" },
};

function pickCrmLabel(
  language: Language,
  labels: Record<string, LocalizedText>,
  key: string,
  fallback: string
) {
  const localized = labels[key];
  return localized ? pickLanguage(language, localized) : fallback;
}

export function getCrmSectionLabel(language: Language, key: string, fallback: string) {
  return pickCrmLabel(language, CRM_SECTION_LABELS, key, fallback);
}

export function getCrmMenuLabel(language: Language, key: string, fallback: string) {
  return pickCrmLabel(language, CRM_MENU_LABELS, key, fallback);
}

export function getCrmDocumentLabel(language: Language, key: string, fallback: string) {
  return pickCrmLabel(language, CRM_DOCUMENT_LABELS, key, fallback);
}
